// Project 8: 발행 이력 컴포넌트
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { publishService } from '@/services/PublishService';
import type { PublishHistoryItem, PublishPlatform, PublishStatus } from '@/types/publish';

export function PublishHistory() {
  const [filters, setFilters] = useState<{
    platform?: PublishPlatform;
    status?: PublishStatus;
    limit?: number;
    offset?: number;
  }>({
    limit: 50,
    offset: 0
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['publish-history', filters],
    queryFn: () => publishService.getHistory(filters),
  });

  const history = data?.data?.history || [];

  const getStatusColor = (status: PublishStatus) => {
    switch (status) {
      case 'success':
        return { color: '#00b894', bg: '#d4edda' };
      case 'failed':
        return { color: '#d63031', bg: '#fee' };
      case 'processing':
        return { color: '#fdcb6e', bg: '#fff3cd' };
      case 'retrying':
        return { color: '#e17055', bg: '#ffe5e5' };
      default:
        return { color: '#999', bg: '#f0f0f0' };
    }
  };

  const getPlatformLabel = (platform: PublishPlatform) => {
    switch (platform) {
      case 'wordpress':
        return '📝 WordPress';
      case 'tistory':
        return '📔 티스토리';
      case 'naver':
        return '📘 네이버 블로그';
      default:
        return platform;
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600 }}>발행 이력</h3>
        <button
          onClick={() => refetch()}
          style={{
            padding: '0.5rem 1rem',
            background: '#f0f0f0',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: '0.875rem'
          }}
        >
          새로고침
        </button>
      </div>

      {/* 필터 */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <select
          value={filters.platform || ''}
          onChange={(e) => setFilters({ ...filters, platform: e.target.value as PublishPlatform || undefined })}
          style={{
            padding: '0.5rem',
            border: '1px solid #ddd',
            borderRadius: 6,
            fontSize: '0.875rem'
          }}
        >
          <option value="">모든 플랫폼</option>
          <option value="wordpress">WordPress</option>
          <option value="tistory">티스토리</option>
          <option value="naver">네이버 블로그</option>
        </select>

        <select
          value={filters.status || ''}
          onChange={(e) => setFilters({ ...filters, status: e.target.value as PublishStatus || undefined })}
          style={{
            padding: '0.5rem',
            border: '1px solid #ddd',
            borderRadius: 6,
            fontSize: '0.875rem'
          }}
        >
          <option value="">모든 상태</option>
          <option value="success">성공</option>
          <option value="failed">실패</option>
          <option value="processing">처리 중</option>
          <option value="pending">대기 중</option>
        </select>
      </div>

      {/* 이력 목록 */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
          로딩 중...
        </div>
      ) : history.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
          발행 이력이 없습니다.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {history.map((item: PublishHistoryItem) => {
            const statusColor = getStatusColor(item.status);

            return (
              <div
                key={item.id}
                style={{
                  padding: '1rem',
                  border: '1px solid #ddd',
                  borderRadius: 8,
                  background: '#fff'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                      {getPlatformLabel(item.platform)}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#666' }}>
                      콘텐츠 ID: {item.content_id}
                    </div>
                  </div>
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    background: statusColor.bg,
                    color: statusColor.color,
                    borderRadius: 4,
                    fontSize: '0.75rem',
                    fontWeight: 600
                  }}>
                    {item.status}
                  </span>
                </div>

                {item.remote_url && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <a
                      href={item.remote_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#2196f3',
                        textDecoration: 'none',
                        fontSize: '0.875rem'
                      }}
                    >
                      발행된 포스트 보기 →
                    </a>
                  </div>
                )}

                {item.error_message && (
                  <div style={{
                    marginTop: '0.5rem',
                    padding: '0.5rem',
                    background: '#fee',
                    borderRadius: 4,
                    fontSize: '0.875rem',
                    color: '#c00'
                  }}>
                    오류: {item.error_message}
                  </div>
                )}

                <div style={{
                  marginTop: '0.5rem',
                  fontSize: '0.75rem',
                  color: '#999'
                }}>
                  {new Date(item.created_at).toLocaleString('ko-KR')}
                  {item.published_at && ` • 발행: ${new Date(item.published_at).toLocaleString('ko-KR')}`}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

